/* ss-bundle-builder — absorb existing cart items
 *
 * Only lines carrying _bundle_id are discounted by the Bundle Builder function. So a shopper
 * who added a rug while browsing and then builds a bundle would pay full price on that rug
 * and a discount on everything else — which reads as a mistake at checkout. This offers to
 * pull those loose lines into the bundle so they count toward the tier and get the discount.
 *
 * It also offers to merge lines from an EARLIER bundle into the current one. Two 2-item
 * bundles at 5% each is a worse outcome for everybody than one 4-item bundle at 20%.
 *
 * Only offers products the builder actually sells (a matching card must exist), which
 * inherits the section's eligibility rules — pre-defined bundle SKUs and excluded tags can
 * never be absorbed. Free gift lines are always left alone.
 *
 * Standalone on purpose: it reaches into the element's state rather than living inside it,
 * so the main script stays untouched.
 */
(function () {
  'use strict';

  var CSS = [
    '.bb__absorb{max-width:760px;margin:0 auto 28px;background:#fff;border:1.5px solid var(--bb-rule,#E3D8CC);border-radius:16px;padding:18px 20px;box-shadow:0 4px 16px rgba(58,44,32,.10)}',
    '.bb__absorb[hidden]{display:none}',
    '.bb__absorb-top{display:flex;align-items:center;gap:14px;margin-bottom:14px}',
    '.bb__absorb-thumbs{display:flex;flex:none}',
    '.bb__absorb-thumbs img{width:46px;height:46px;object-fit:cover;border-radius:8px;border:2px solid #fff;box-shadow:0 1px 3px rgba(58,44,32,.16)}',
    '.bb__absorb-thumbs img+img{margin-left:-14px}',
    '.bb__absorb-copy{min-width:0}',
    '.bb__absorb-t{margin:0 0 3px;font-family:var(--heading-font-family,inherit);font-weight:700;font-size:15px;color:var(--bb-ink,#3A2C20)}',
    '.bb__absorb-s{margin:0;font-size:13px;line-height:1.45;color:var(--bb-muted,#6B5A48)}',
    '.bb__absorb-actions{display:flex;gap:10px;flex-wrap:wrap}',
    '.bb__absorb-yes,.bb__absorb-no{border-radius:99px;font-family:var(--heading-font-family,inherit);font-weight:700;font-size:12.5px;cursor:pointer;padding:11px 20px;line-height:1;display:flex;align-items:center;justify-content:center;text-align:center}',
    '.bb__absorb-yes{flex:1;min-width:180px;background:var(--bb-ink,#3A2C20);color:#fff;border:1.5px solid var(--bb-ink,#3A2C20);box-shadow:0 3px 10px rgba(58,44,32,.22)}',
    '.bb__absorb-yes:hover{background:#fff;color:var(--bb-ink,#3A2C20)}',
    '.bb__absorb-no{background:transparent;color:var(--bb-muted,#6B5A48);border:1.5px solid var(--bb-rule,#E3D8CC)}',
    '.bb__absorb-no:hover{border-color:var(--bb-ink,#3A2C20);color:var(--bb-ink,#3A2C20)}',
    '@media(max-width:520px){.bb__absorb-yes,.bb__absorb-no{width:100%;flex:none}}',
    '.bb__absorb--mini{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:10px 8px 10px 16px;border-radius:99px;box-shadow:none;background:var(--bb-soft,#F7F1E6);border-color:var(--bb-rule,#E3D8CC)}',
    '.bb__absorb-reopen{flex:1;border:0;background:none;color:var(--bb-ink,#3A2C20);font-family:var(--heading-font-family,inherit);font-weight:700;font-size:13px;cursor:pointer;text-align:left;padding:0}',
    '.bb__absorb-reopen:hover{text-decoration:underline}',
    '.bb__absorb-x{flex:none;border:0;background:none;color:var(--bb-muted,#6B5A48);font-size:20px;line-height:1;cursor:pointer;padding:0 8px}',
    '.bb__absorb-x:hover{color:var(--bb-ink,#3A2C20)}'
  ].join('');

  function injectCss() {
    if (document.getElementById('bb-absorb-css')) return;
    var s = document.createElement('style');
    s.id = 'bb-absorb-css';
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  function money(c) { return '$' + (c / 100).toFixed(2); }

  function init(bb) {
    if (!bb || bb._absorbReady || !bb.cards || !bb.cards.length) return;
    bb._absorbReady = true;

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = cart.items || [];
        // Everything except free gifts is fair game, including lines already in an earlier
        // bundle — merging them into one build reaches a better tier for everyone.
        var loose = items.filter(function (i) {
          var p = i.properties || {};
          return !p._gift;
        });
        if (!loose.length) return;

        var matched = [];
        loose.forEach(function (i) {
          for (var k = 0; k < bb.cards.length; k++) {
            if (parseInt(bb.cards[k].dataset.vid, 10) === i.variant_id) {
              matched.push({ line: i, card: bb.cards[k] });
              return;
            }
          }
        });
        if (!matched.length) return;

        var anyBundled = matched.some(function (m) {
          return (m.line.properties || {})._bundle_id;
        });
        render(bb, matched, cart.item_count || 0, anyBundled);
      })
      .catch(function () {});
  }

  function render(bb, matched, cartCount, anyBundled) {
    injectCss();

    var n = matched.reduce(function (t, m) { return t + m.line.quantity; }, 0);
    var value = matched.reduce(function (t, m) { return t + m.line.original_line_price; }, 0);

    var total = cartCount || n;
    var partial = total > n;

    var heading = anyBundled
      ? 'You have a bundle in progress'
      : 'You already have ' + total + ' item' + (total === 1 ? '' : 's') + ' in your cart';

    var body;
    if (anyBundled) {
      body = partial
        ? n + ' of them can join this bundle (' + money(value) + ') so everything counts ' +
          'toward one bigger discount instead of separate ones.'
        : 'Add ' + (n === 1 ? 'it' : 'them') + ' to this bundle (' + money(value) + ') so ' +
          'everything counts toward one bigger discount instead of separate ones.';
    } else {
      body = partial
        ? n + ' of them can join your bundle (' + money(value) + ') and count toward your discount. ' +
          'The rest stay in your cart at full price.'
        : 'Add ' + (n === 1 ? 'it' : 'them') + ' to your bundle (' + money(value) + ') and ' +
          (n === 1 ? 'it counts' : 'they count') + ' toward your discount. Otherwise ' +
          (n === 1 ? 'it stays' : 'they stay') + ' in your cart at full price.';
    }

    var thumbs = matched.slice(0, 4).map(function (m) {
      var src = m.card.dataset.img || '';
      return src ? '<img src="' + src + '" alt="" width="46" height="46" loading="lazy">' : '';
    }).join('');

    var el = document.createElement('div');
    el.className = 'bb__absorb';
    el.setAttribute('data-bb-absorb', '');
    el.innerHTML =
      '<div class="bb__absorb-top">' +
        '<span class="bb__absorb-thumbs">' + thumbs + '</span>' +
        '<span class="bb__absorb-copy">' +
          '<p class="bb__absorb-t">' + heading + '</p>' +
          '<p class="bb__absorb-s">' + body + '</p>' +
        '</span>' +
      '</div>' +
      '<div class="bb__absorb-actions">' +
        '<button type="button" class="bb__absorb-yes">' + (partial ? 'Add ' + n + ' to my bundle' : 'Add to my bundle') + '</button>' +
        '<button type="button" class="bb__absorb-no">No thanks</button>' +
      '</div>';

    var mount = bb.querySelector('.bb__layout');
    if (!mount) return;
    mount.parentNode.insertBefore(el, mount);

    el.querySelector('.bb__absorb-yes').addEventListener('click', function () {
      absorb(bb, matched);
      el.remove();
    });

    // "No thanks" collapses to a one-line link rather than disappearing. Someone who
    // declines up front often changes their mind once they see how close a tier is, and
    // there would otherwise be no way back short of reloading the page.
    el.querySelector('.bb__absorb-no').addEventListener('click', function () {
      el.classList.add('bb__absorb--mini');
      el.innerHTML =
        '<button type="button" class="bb__absorb-reopen">+ Add your ' + n + ' cart item' +
        (n === 1 ? '' : 's') + ' to this bundle</button>' +
        '<button type="button" class="bb__absorb-x" aria-label="Dismiss">&times;</button>';
      el.querySelector('.bb__absorb-reopen').addEventListener('click', function () {
        absorb(bb, matched);
        el.remove();
      });
      el.querySelector('.bb__absorb-x').addEventListener('click', function () { el.remove(); });
    });
  }

  function absorb(bb, matched) {
    // Remember the original cart lines so addBundle can clear them before re-adding under
    // one _bundle_id — otherwise the shopper ends up with the item twice, or with two
    // separate bundles that each earn a smaller discount.
    bb._absorbedKeys = (bb._absorbedKeys || []).concat(matched.map(function (m) { return m.line.key; }));

    matched.forEach(function (m) {
      var d = m.card.dataset;
      var existing = bb.sel.get(d.vid);
      if (existing) {
        existing.qty += m.line.quantity;   // already picked in the builder — merge, don't stack
      } else {
        bb.sel.set(d.vid, {
          vid: d.vid,
          qty: m.line.quantity,
          price: parseInt(d.price, 10) || 0,
          title: d.title,
          handle: d.handle,
          cat: d.cat,
          img: d.img
        });
      }
    });

    patchAdd(bb);
    bb.render();
  }

  /* Wrap addBundle so the absorbed lines are cleared from the cart first. They are re-added
     by the normal flow, this time all carrying the same new _bundle_id.

     One /cart/update.js call, not a fetch per key: sequential per-key /cart/change.js
     removals proved unreliable in testing — a run would drop one line and silently miss
     another, leaving a stale bundle line and the product duplicated, which is the exact
     failure this feature exists to prevent. update.js applies every change in one request,
     so there is no ordering to get wrong. */
  function patchAdd(bb) {
    if (bb._absorbPatched) return;
    bb._absorbPatched = true;

    var original = bb.addBundle.bind(bb);
    bb.addBundle = function () {
      var keys = bb._absorbedKeys || [];
      if (!keys.length) return original();

      var updates = {};
      keys.forEach(function (k) { updates[k] = 0; });

      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: updates })
      })
        .catch(function () {})
        .then(function () {
          bb._absorbedKeys = [];
          original();
        });
    };
  }

  function boot() {
    document.querySelectorAll('bundle-builder').forEach(function (bb) {
      if (bb.cards && bb.cards.length) init(bb);
      else setTimeout(function () { init(bb); }, 400);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
